import React from 'react';
import { Icon, Loader, Table } from 'semantic-ui-react';
import { ClientObservationEntryDto } from '../../services/contracts/ContractService';

const formatObservationDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} - ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const truncate = (value: string | null | undefined, max: number) => {
  const text = (value ?? '').trim();
  if (!text) return '—';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
};

export interface ClientObservationsSummaryProps {
  observations: ClientObservationEntryDto[];
  loading?: boolean;
  maxRows?: number;
  maxHeight?: number;
  emptyText?: string;
  dense?: boolean;
}

const ClientObservationsSummary: React.FC<ClientObservationsSummaryProps> = ({
  observations,
  loading = false,
  maxRows = 5,
  maxHeight = 200,
  emptyText = 'Nenhuma observação registrada.',
  dense = false,
}) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: dense ? '10px 0' : '16px 0', color: '#888' }}>
        <Loader active inline size="small" />
        <span style={{ marginLeft: 8, fontSize: '0.85rem' }}>Carregando observações…</span>
      </div>
    );
  }

  const rows = observations.slice(0, maxRows);
  if (rows.length === 0) {
    return (
      <div style={{ fontSize: '0.85rem', color: '#888', padding: dense ? '4px 0' : '8px 0' }}>
        <Icon name="comment outline" style={{ opacity: 0.5 }} />
        <span style={{ marginLeft: 6 }}>{emptyText}</span>
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1px solid #e1bee7',
        borderRadius: 8,
        overflow: 'auto',
        maxHeight,
        background: '#fff',
      }}
    >
      <Table celled striped compact size="small" style={{ margin: 0, fontSize: dense ? '0.78rem' : '0.82rem' }}>
        <Table.Header>
          <Table.Row style={{ background: '#f3e5f5' }}>
            <Table.HeaderCell width={3}>Data</Table.HeaderCell>
            <Table.HeaderCell width={3}>Colaborador</Table.HeaderCell>
            <Table.HeaderCell width={4}>Ocorrência</Table.HeaderCell>
            <Table.HeaderCell width={6}>Observação</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((entry) => (
            <Table.Row key={entry.id}>
              <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                {formatObservationDate(entry.occurredAtUtc)}
              </Table.Cell>
              <Table.Cell style={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
                {truncate(entry.collaboratorName, 28)}
              </Table.Cell>
              <Table.Cell title={entry.occurrence}>{truncate(entry.occurrence, dense ? 40 : 60)}</Table.Cell>
              <Table.Cell title={entry.observation ?? undefined}>
                {truncate(entry.observation, dense ? 50 : 80)}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {observations.length > maxRows && (
        <div style={{ padding: '6px 10px', fontSize: '0.75rem', color: '#7b1fa2', background: '#faf5ff' }}>
          + {observations.length - maxRows} observação(ões) anterior(es) — veja o histórico completo do cliente.
        </div>
      )}
    </div>
  );
};

export default ClientObservationsSummary;
